import { toastService } from '@/app/services/toastService';
import { useCreateMailSubscriptionMutation } from '@/b2b/shared/api/amo/useSubscription';
import { COLORS, TYPO } from '@/b2b/shared/consts';
import { Mail24 } from '@/b2b/shared/icons';
import { BreakpointsEnum } from '@/b2b/shared/types';
import { UiFlex } from '@/b2b/shared/ui';
import { FC, useState } from 'react';
import styled from 'styled-components';

export const SubscriptionBlock: FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [createMailSubscription] = useCreateMailSubscriptionMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !/^[^\s@]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/.test(email)) {
      toastService.error(
        {
          title: 'Введите корректный email',
        },
        {
          autoClose: 3000,
        },
      );
      return;
    }

    try {
      const { status } = await createMailSubscription(email).unwrap();

      if (status) {
        toastService.success(
          {
            title: 'Вы успешно подписались на рассылку',
          },
          {
            autoClose: 3000,
          },
        );
        setSubmitted(true);
        setEmail('');
      } else {
        toastService.error(
          {
            title: 'Произошла ошибка при подписке',
          },
          {
            autoClose: 3000,
          },
        );
      }
    } catch (_error) {
      toastService.error(
        {
          title: 'Произошла ошибка при подписке',
        },
        {
          autoClose: 3000,
        },
      );
    }
  };

  const isDisabled = !email || submitted;

  return (
    <Root
      direction="column"
      gap={24}
    >
      <Title>Рассылка</Title>

      <Form onSubmit={handleSubmit}>
        <InputContainer gap={8}>
          <Input
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            placeholder="Введите email"
            disabled={submitted}
          />
          <SubmitButton
            type="submit"
            disabled={isDisabled}
          >
            <Mail24 />
          </SubmitButton>
        </InputContainer>
      </Form>

      <SubText>Подпишитесь на нашу рассылку, чтобы получать полезные советы и новости о ментальном здоровье</SubText>
    </Root>
  );
};

const Root = styled(UiFlex)`
  width: 100%;

  @media (min-width: ${BreakpointsEnum.lgm}px) {
    width: 290px;
  }
`;

const Title = styled.p`
  ${TYPO.subtitle1}
  color: ${COLORS.black};
`;

const Form = styled.form`
  width: 100%;
`;

const InputContainer = styled(UiFlex)`
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 24px;
  border: 1px solid ${COLORS.grayscale200};
  background-color: ${COLORS.white};
  ${TYPO.body2}
  color: ${COLORS.black};

  &::placeholder {
    color: ${COLORS.caption};
  }

  &:disabled {
    background-color: ${COLORS.grayscale50};
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  border-radius: 50%;
  border: none;
  background-color: ${COLORS.grayscale50};
  color: ${COLORS.black};
  cursor: pointer;
  padding: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    opacity: 0.8;
  }
`;

const SubText = styled.p`
  ${TYPO.caption1}
  color: ${COLORS.caption};
`;
